import { Alert, Button, Grid, TextField, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { RequireAuth, useFusionAuth } from "@fusionauth/react-sdk";
import { useFetch } from "../../../services/hooks/useFetch";
import CohortUpload from "../../../components/CohortUpload";
import { apiPrefix } from "../../../utils/main";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { ProjectContext } from "../../../contexts/ProjectContext";
import CloseArrowTooltip from "../../../components/CloseArrowTooltip";
import { HTTPMethod } from "@fusionauth/typescript-client";
import CheckIconOutlined from "@mui/icons-material/CheckOutlined";

dayjs.extend(timezone);
dayjs.extend(utc);

export default function PatientCohortPage() {
  const project = useContext(ProjectContext)!;
  const { userInfo } = useFusionAuth();
  const isAdmin = userInfo?.roles?.includes("ADMIN");

  const { data: retrievedCohort, fetch: getProjectCohort } =
    useFetch<CohortData[]>();

  const {
    update: saveCohort,
    error: saveCohortError,
    status: saveCohortStatus,
  } = useFetch<undefined, CohortDataUpdate, CohortData[]>();

  const [currentCohort, setCurrentCohort] = useState<CohortData[]>([]);
  const [editable, setEditable] = useState(false);
  const [updateDetails, setUpdateDetails] = useState("");
  const [displayErrors, setDisplayErrors] = useState(false);
  const [cohortValidated, setCohortValidated] = useState(false);

  const isSaveDisabled =
    !cohortValidated || displayErrors || updateDetails === "";

  useEffect(() => {
    if (isAdmin) {
      void getProjectCohort(`${apiPrefix}/projects/${project.id}/cohort`);
    }
  }, [project, getProjectCohort, isAdmin]);

  useEffect(() => {
    let initialLoad = true;
    if (initialLoad && retrievedCohort) {
      setCurrentCohort(retrievedCohort);
    }
    return () => {
      initialLoad = false;
    };
  }, [retrievedCohort]);

  const updateCohortGrid = function (cohortList: CohortData[]) {
    const hasCohortListErrors = cohortList?.some((patient) => patient.errors);
    setDisplayErrors(!!hasCohortListErrors);
    setCurrentCohort(cohortList);
  };

  const updateCohort = function () {
    const hasCohortListErrors = currentCohort?.some(
      (patient) => patient.errors,
    );
    setDisplayErrors(!!hasCohortListErrors);

    if (!hasCohortListErrors) {
      void saveCohort({
        path: `${apiPrefix}/projects/${project.id}/cohort`,
        method: HTTPMethod.POST,
        body: {
          reason: updateDetails,
          cohortList: currentCohort,
        },
        responseManipulationFunction: (data) => {
          setCurrentCohort(data);
        },
      });
    }
  };

  useEffect(() => {
    if (saveCohortStatus) {
      if (saveCohortStatus === 200) {
        setUpdateDetails("");
        setEditable(false);
        setDisplayErrors(false);
      } else {
        setDisplayErrors(true);
      }
    }
  }, [saveCohortStatus]);

  return (
    <Grid container direction={"column"} className="company-settings-item">
      <div className="company-settings-item">
        <Grid>
          <Typography variant="h4">Patient Cohort</Typography>
        </Grid>
        <br />
        <Grid>
          <Typography variant="body2">
            To change the patient cohort for this project, click the edit button
            below and follow the steps to upload and
            <br />
            validate a new patient cohort file.
          </Typography>
        </Grid>
        <br />
        <RequireAuth withRole={"ADMIN"}>
          <Grid>
            <center>
              <CohortUpload
                editable={editable}
                cohortListInitiallyExpanded={true}
                cohortList={currentCohort}
                onUpdateData={updateCohortGrid}
                setCohortValidated={setCohortValidated}
              />
            </center>
          </Grid>
        </RequireAuth>
        <br />

        {editable && cohortValidated && (
          <Grid>
            <Alert icon={<CheckIconOutlined />} severity="success">
              The source data has been validated!
            </Alert>
            <br />
          </Grid>
        )}

        {editable && (
          <Grid>
            <TextField
              required
              label="Note/Reason for Updating Cohort"
              multiline
              minRows={2}
              sx={{ width: "100%" }}
              slotProps={{
                htmlInput: {
                  readOnly: !editable,
                },
              }}
              value={updateDetails}
              error={displayErrors && updateDetails === ""}
              helperText={
                displayErrors && updateDetails === ""
                  ? "This field is required"
                  : ""
              }
              onChange={(event) => {
                setUpdateDetails(event.target.value);
              }}
            ></TextField>
          </Grid>
        )}
        <br />
        {!!saveCohortError && displayErrors && (
          <>
            <Alert severity="error">
              {typeof saveCohortError === "object" &&
              "message" in saveCohortError
                ? String(saveCohortError.message)
                : String(saveCohortError)}
            </Alert>
            <br />
          </>
        )}
        <Grid container justifyContent={"right"}>
          {(editable && (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setUpdateDetails("");
                  setEditable(false);
                  setDisplayErrors(false);
                  if (retrievedCohort) {
                    setCurrentCohort(retrievedCohort);
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className="update-button"
                sx={{ marginLeft: "5px" }}
                disabled={isSaveDisabled}
                onClick={() => {
                  updateCohort();
                }}
              >
                Update
              </Button>
            </>
          )) || (
            <CloseArrowTooltip
              title={project.isLocked ? "This project is locked." : ""}
            >
              <span>
                <Button
                  variant="contained"
                  onClick={() => setEditable(true)}
                  disabled={project.isLocked}
                >
                  Edit
                </Button>
              </span>
            </CloseArrowTooltip>
          )}
        </Grid>
      </div>
    </Grid>
  );
}
